import React from 'react';
import { Image, Text, View } from '@react-pdf/renderer';

// ============= Types =============
export type PdfListItem = {
  content: PdfElement[];
  nestedList?: PdfElement;
};

export type PdfElement =
  | { type: 'text'; content: string; style?: string }
  | { type: 'heading'; level: number; content: string }
  | { type: 'image'; src: string | null; alt?: string; width?: string | number; height?: string | number }
  | { type: 'paragraph'; children: PdfElement[] }
  | { type: 'list'; ordered: boolean; items: PdfListItem[]; startNumber?: number }
  | { type: 'code'; content: string; language?: string }
  | { type: 'blockquote'; content: string }
  | { type: 'break' };

// ============= IMAGE PROCESSING UTILITIES =============
class ImageProcessor {
  private cache: Map<string, string | null>;
  private processingQueue: Map<string, Promise<string | null>>;

  constructor() {
    this.cache = new Map();
    this.processingQueue = new Map();
  }

  async processImage(imageUrl: string, options: { width?: number; height?: number } = {}): Promise<string | null> {
    if (!imageUrl) return null;
    if (this.cache.has(imageUrl)) {
      return this.cache.get(imageUrl) ?? null;
    }
    if (this.processingQueue.has(imageUrl)) {
      return this.processingQueue.get(imageUrl) ?? null;
    }
    const processingPromise = this._processImageInternal(imageUrl, options);
    this.processingQueue.set(imageUrl, processingPromise);
    try {
      const result = await processingPromise;
      this.cache.set(imageUrl, result);
      return result;
    } finally {
      this.processingQueue.delete(imageUrl);
    }
  }

  async _processImageInternal(imageUrl: string, options: { width?: number; height?: number }): Promise<string | null> {
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    const canvasResult = await this._tryCanvasMethod(imageUrl);
    if (canvasResult) return canvasResult;
    const proxyResult = await this._tryProxyMethod(imageUrl);
    if (proxyResult) return proxyResult;
    return this._generatePlaceholder(imageUrl, options);
  }

  async _tryCanvasMethod(imageUrl: string): Promise<string | null> {
    return new Promise((resolve) => {
      if (typeof window === 'undefined') return resolve(null);
      const img = new window.Image();
      const timeoutId = setTimeout(() => resolve(null), 2000);
      img.onload = () => {
        clearTimeout(timeoutId);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          const maxWidth = 2048;
          const maxHeight = 2048;
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const dataURL = canvas.toDataURL('image/jpeg', 0.95);
          resolve(dataURL);
        } catch (error) {
          resolve(null);
        }
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        resolve(null);
      };
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    });
  }

  async _tryProxyMethod(imageUrl: string): Promise<string | null> {
    try {
      // Use our own backend proxy to fetch Azure Blob images server-side (bypasses CORS completely)
      const backendBase = process.env.REACT_APP_BACKEND_BASE_URL || '';
      const proxyUrl = `${backendBase}/v2/uploads/proxy-image?url=${encodeURIComponent(imageUrl)}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout to prevent UI freezes on slow/broken images

      const response = await fetch(proxyUrl, {
        method: 'GET',
        credentials: 'include', // send auth cookies
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) return null;
      const json = await response.json();
      if (json?.dataUri) return json.dataUri;
      return null;
    } catch (error) {
      return null;
    }
  }

  _generatePlaceholder(originalUrl: string, options: { width?: number; height?: number } = {}): string | null {
    if (typeof window === 'undefined') return null;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const width = options.width || 300;
    const height = options.height || 200;
    canvas.width = width;
    canvas.height = height;
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#f8f9fa');
    gradient.addColorStop(1, '#e9ecef');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);
    ctx.fillStyle = '#6c757d';
    ctx.font = '24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('🖼️', width / 2, height / 2 - 20);
    ctx.font = '12px Arial';
    ctx.fillStyle = '#495057';
    ctx.fillText('Image not available', width / 2, height / 2 + 10);
    try {
      const domain = new URL(originalUrl).hostname;
      ctx.font = '10px Arial';
      ctx.fillStyle = '#6c757d';
      ctx.fillText(domain, width / 2, height / 2 + 25);
    } catch {}
    return canvas.toDataURL('image/png');
  }

  clearCache(): void {
    this.cache.clear();
  }

  // Check if a URL is already cached (for sync contexts)
  getCached(imageUrl: string): string | null | undefined {
    return this.cache.get(imageUrl);
  }
}

const imageProcessor = new ImageProcessor();

/**
 * Pre-warms the image processor cache for all <img src> URLs found in any
 * number of HTML strings. Call this BEFORE generating the PDF so that the
 * sync parser can retrieve base64 data URIs from the cache instead of
 * passing raw Azure Blob URLs to react-pdf (which hits CORS).
 */
export async function preWarmImages(htmlStrings: (string | undefined | null)[]): Promise<void> {
  if (typeof window === 'undefined') return;
  const parser = new window.DOMParser();
  const urls = new Set<string>();

  for (const html of htmlStrings) {
    if (!html || typeof html !== 'string') continue;
    try {
      const doc = parser.parseFromString(html, 'text/html');
      doc.querySelectorAll('img[src]').forEach(img => {
        const src = img.getAttribute('src');
        if (src && !src.startsWith('data:')) urls.add(src);
      });
    } catch { /* ignore parse errors */ }
  }

  await Promise.all(Array.from(urls).map(url => imageProcessor.processImage(url)));
}


// ============= HTML PARSING =============
export async function parseHtmlToElements(htmlString: string): Promise<PdfElement[]> {
  if (!htmlString) return [];
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // SSR fallback: strip tags, return as text
    return [{ type: 'text', content: htmlString.replace(/<[^>]+>/g, '') }];
  }
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements: PdfElement[] = [];

  const processNodeAsync = async (node: Node, container: PdfElement[] = elements, activeStyles = ''): Promise<void> => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.textContent ?? '';
      // Normalize internal whitespace but preserve single spaces at boundaries
      const normalized = raw.replace(/\s+/g, ' ');
      if (normalized.trim().length > 0) {
        container.push({ type: 'text', content: normalized, style: activeStyles || undefined });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      let newStyles = activeStyles;
      const styleAttr = el.getAttribute('style') || '';
      if (styleAttr) {
        if (/text-decoration(-line)?:\s*underline/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
        }
        if (/text-decoration(-line)?:\s*line-through/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
        }
        if (/font-weight:\s*(bold|700)/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
        }
        if (/font-style:\s*italic/i.test(styleAttr)) {
          newStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
        }
      }

      switch (el.tagName.toLowerCase()) {
        case 'img': {
          const src = el.getAttribute('src');
          const width = el.getAttribute('width');
          const height = el.getAttribute('height');
          const processedSrc = await imageProcessor.processImage(src ?? '', {
            width: width ? parseInt(width) : undefined,
            height: height ? parseInt(height) : undefined
          });
          container.push({
            type: 'image',
            src: processedSrc,
            alt: el.getAttribute('alt') || '',
            width: width ?? undefined,
            height: height ?? undefined,
          });
          break;
        }
        case 'p': {
          const paragraph: PdfElement = { type: 'paragraph', children: [] };
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, paragraph.children, newStyles);
          }
          if (paragraph.children.length > 0) {
            container.push(paragraph);
          }
          break;
        }
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          container.push({
            type: 'heading',
            level: parseInt(el.tagName.substring(1)),
            content: el.textContent?.trim() ?? ''
          });
          break;
        case 'strong':
        case 'b': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'bold';
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container, combinedStyles);
          }
          break;
        }
        case 'em':
        case 'i': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'italic';
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container, combinedStyles);
          }
          break;
        }
        case 'u':
        case 'ins': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'underline';
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container, combinedStyles);
          }
          break;
        }
        case 'strike':
        case 's':
        case 'del': {
          const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'strikethrough';
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container, combinedStyles);
          }
          break;
        }
        case 'span': {
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container, newStyles);
          }
          break;
        }
        case 'ul':
        case 'ol': {
          const isOrdered = el.tagName.toLowerCase() === 'ol';
          const startAttr = el.getAttribute('start');
          const startNumber = startAttr ? parseInt(startAttr, 10) : 1;

          const list: PdfElement & { startNumber?: number } = {
            type: 'list',
            ordered: isOrdered,
            items: [],
            startNumber: isOrdered ? startNumber : undefined
          };

          // Process direct children - can be <li> or nested <ul>/<ol> (invalid HTML but common from Word)
          let lastListItem: PdfListItem | null = null;

          for (const child of Array.from(el.children)) {
            const childTag = (child as HTMLElement).tagName.toLowerCase();

            // Handle nested list that's a direct child of the parent list (Word-style HTML)
            if (childTag === 'ul' || childTag === 'ol') {
              // Attach this nested list to the last list item
              if (lastListItem) {
                const nestedList: PdfElement = {
                  type: 'list',
                  ordered: childTag === 'ol',
                  items: []
                };
                for (const nestedLi of Array.from((child as HTMLElement).children).filter(c => (c as HTMLElement).tagName.toLowerCase() === 'li')) {
                  const nestedItem: PdfListItem = { content: [] };
                  for (const nestedChildNode of Array.from(nestedLi.childNodes)) {
                    await processNodeAsync(nestedChildNode, nestedItem.content);
                  }
                  nestedList.items.push(nestedItem);
                }
                lastListItem.nestedList = nestedList;
              }
              continue;
            }

            // Handle regular <li> elements
            if (childTag !== 'li') continue;

            const li = child as HTMLElement;
            const listItem: PdfListItem = { content: [] };
            lastListItem = listItem;

            // Process the content of this <li>, looking for nested lists
            for (const childNode of Array.from(li.childNodes)) {
              if (childNode.nodeType === Node.ELEMENT_NODE) {
                const childEl = childNode as HTMLElement;
                const tagName = childEl.tagName.toLowerCase();

                // If we find a nested list, store it separately
                if (tagName === 'ul' || tagName === 'ol') {
                  const nestedList: PdfElement = {
                    type: 'list',
                    ordered: tagName === 'ol',
                    items: []
                  };
                  // Recursively process nested list items
                  for (const nestedLi of Array.from(childEl.children).filter(child => child.tagName.toLowerCase() === 'li')) {
                    const nestedItem: PdfListItem = { content: [] };
                    // Process nested li content - iterate through child nodes
                    for (const nestedChildNode of Array.from(nestedLi.childNodes)) {
                      await processNodeAsync(nestedChildNode, nestedItem.content);
                    }
                    nestedList.items.push(nestedItem);
                  }
                  listItem.nestedList = nestedList;
                } else {
                  // Process other elements normally (text, bold, italic, etc.)
                  await processNodeAsync(childNode, listItem.content);
                }
              } else if (childNode.nodeType === Node.TEXT_NODE) {
                const text = childNode.textContent?.replace(/\s+/g, ' ').trim() ?? '';
                if (text && text !== '\u00a0') { // Skip non-breaking spaces
                  listItem.content.push({ type: 'text', content: text });
                }
              }
            }

            list.items.push(listItem);
          }
          if (list.items.length > 0) {
            container.push(list);
          }
          break;
        }
        case 'pre':
          container.push({
            type: 'code',
            content: el.textContent?.trim() ?? '',
            language: el.getAttribute('data-language') || 'code'
          } as any);
          break;
        case 'code': {
          const isInline = !el.closest('pre');
          if (isInline) {
            const combinedStyles = (newStyles ? newStyles + ' ' : '') + 'monospace';
            for (const child of Array.from(el.childNodes)) {
              await processNodeAsync(child, container, combinedStyles);
            }
          } else {
            container.push({
              type: 'code',
              content: el.textContent?.trim() ?? '',
              language: el.getAttribute('data-language') || 'code'
            } as any);
          }
          break;
        }
        case 'blockquote':
          container.push({
            type: 'blockquote',
            content: el.textContent?.trim() ?? ''
          } as any);
          break;
        case 'br':
          container.push({ type: 'break' });
          break;
        default:
          for (const child of Array.from(el.childNodes)) {
            await processNodeAsync(child, container);
          }
          break;
      }
    }
  };

  for (const child of Array.from(doc.body.childNodes)) {
    await processNodeAsync(child);
  }
  return elements;
}

// ============= PDF RENDERER =============
// renderPdfElements returns an array of React elements for use directly in PDF rendering. Do not wrap its output in <Text>.
// If you need a plain text fallback, use renderPdfElementsPlainText.
export function renderPdfElements(elements: PdfElement[]): (JSX.Element | null)[] {
  // Helper function to check if an element is an image
  const isImageElement = (el: PdfElement): boolean => {
    return el.type === 'image' || (el.type === 'paragraph' && el.children.length === 1 && el.children[0].type === 'image');
  };

  // Helper function to get optimized image dimensions
  const getImageDimensions = (element: PdfElement & { type: 'image' }): { width?: number; height?: number; maxWidth: number; maxHeight: number } => {
    const maxWidth = 400;
    const maxHeight = 300; // Increased from 200 to allow for better image quality

    let width = typeof element.width === 'number' ? element.width : (element.width ? parseInt(element.width as string) : undefined);
    let height = typeof element.height === 'number' ? element.height : (element.height ? parseInt(element.height as string) : undefined);

    // If both width and height are specified, respect aspect ratio
    if (width && height) {
      const aspectRatio = width / height;
      if (width > maxWidth) {
        width = maxWidth;
        height = width / aspectRatio;
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }
    }

    return { width, height, maxWidth, maxHeight };
  };

  // Helper function to get smart margins for images
  const getImageMargins = (currentIndex: number): { marginTop: number; marginBottom: number } => {
    const prevElement = currentIndex > 0 ? elements[currentIndex - 1] : null;
    const nextElement = currentIndex < elements.length - 1 ? elements[currentIndex + 1] : null;

    const isPrevImage = prevElement && isImageElement(prevElement);
    const isNextImage = nextElement && isImageElement(nextElement);

    // Check if this is the first or last element
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === elements.length - 1;

    return {
      marginTop: isFirst ? 12 : (isPrevImage ? 6 : 14),      // Smaller gap between consecutive images
      marginBottom: isLast ? 12 : (isNextImage ? 6 : 14)     // Smaller gap between consecutive images
    };
  };

  return elements.map((element: PdfElement, index: number): JSX.Element | null => {
    if (element.type === 'text') {
      const style: any = {
        textAlign: 'justify',
        lineHeight: 1.60,
        fontSize: 11.5,
      };
      if (element.style) {
        const stylesList = element.style.split(' ');
        if (stylesList.includes('bold')) style.fontWeight = 'bold';
        if (stylesList.includes('italic')) style.fontStyle = 'italic';
        if (stylesList.includes('underline')) style.textDecoration = 'underline';
        if (stylesList.includes('strikethrough')) style.textDecoration = 'line-through';
      }
      return (
        <Text key={index} style={style}>{element.content}</Text>
      );
    } else if (element.type === 'heading') {
      const fontSize = element.level === 1 ? 24 : element.level === 2 ? 20 : 16;
      const fontWeight = 'bold' as const;
      const marginBottom = element.level === 1 ? 16 : element.level === 2 ? 12 : 10;
      const marginTop = element.level === 1 ? 20 : element.level === 2 ? 16 : 12;
      return (
        <Text key={index} style={{ fontSize, fontWeight, marginBottom, marginTop }}>{element.content}</Text>
      );
    } else if (element.type === 'code') {
      return (
        <View key={index} style={{
          backgroundColor: '#1e293b',
          padding: 12,
          marginVertical: 8,
          borderRadius: 4,
          borderLeft: '4px solid #3b82f6'
        }}>
          <Text style={{
            fontFamily: 'Courier',
            fontSize: 10,
            color: '#e2e8f0',
            lineHeight: 1.4
          }}>
            {element.content}
          </Text>
        </View>
      );
    } else if (element.type === 'blockquote') {
      return (
        <View key={index} style={{
          borderLeft: '5px solid #e5e7eb',
          paddingLeft: 16,
          paddingVertical: 8,
          marginVertical: 8,
          backgroundColor: '#f9fafb'
        }}>
          <Text style={{
            fontStyle: 'italic',
            color: '#6b7280',
            fontSize: 12
          }}>
            {element.content}
          </Text>
        </View>
      );
    } else if (element.type === 'image') {
      if (!element.src) return null;
      const imageMargins = getImageMargins(index);
      const imageDimensions = getImageDimensions(element);
      return (
        <Image
          key={index}
          style={{
            maxWidth: imageDimensions.maxWidth,
            maxHeight: imageDimensions.maxHeight,
            marginTop: imageMargins.marginTop,
            marginBottom: imageMargins.marginBottom,
            width: imageDimensions.width,
            height: imageDimensions.height
          }}
          src={element.src}
        />
      );
    } else if (element.type === 'paragraph') {
      // Check if paragraph contains only an image
      const hasOnlyImage = element.children.length === 1 && element.children[0].type === 'image';

      if (hasOnlyImage) {
        // For image-only paragraphs, apply smart spacing and render image without additional margins
        const imageMargins = getImageMargins(index);
        const imageChild = element.children[0];

        // Type guard to ensure we have an image element
        if (imageChild.type === 'image') {
          const imageDimensions = getImageDimensions(imageChild);
          return (
            <View key={index} style={{
              marginTop: imageMargins.marginTop,
              marginBottom: imageMargins.marginBottom
            }}>
              {imageChild.src ? (
                <Image
                  style={{
                    maxWidth: imageDimensions.maxWidth,
                    maxHeight: imageDimensions.maxHeight,
                    width: imageDimensions.width,
                    height: imageDimensions.height
                  }}
                  src={imageChild.src}
                />
              ) : null}
            </View>
          );
        } else {
          // Fallback for non-image content in supposedly image-only paragraph
          return (
            <View key={index} style={{ marginBottom: 4 }}>
              {renderPdfElements(element.children)}
            </View>
          );
        }
      } else {
        // If paragraph contains any non-inline block element, fallback to block rendering
        const hasBlockChild = element.children.some((child: any) => (
          child.type === 'image' || child.type === 'list' || child.type === 'code' || child.type === 'blockquote' || child.type === 'heading'
        ));
        if (hasBlockChild) {
          return (
            <View key={index} style={{
              marginBottom: 8,
              textAlign: 'justify',
              lineHeight: 1.60,
              fontSize: 11.5
            }}>
              {renderPdfElements(element.children)}
            </View>
          );
        }
        // Inline paragraph: render once as a single Text with nested runs to preserve flow and spacing
        return (
          <Text key={index} style={{ marginBottom: 8, textAlign: 'justify', lineHeight: 1.60, fontSize: 11.5 }}>
            {(() => {
              const pieces: Array<{ style?: any; content: string } | { break: true }> = [];
              element.children.forEach((child: any) => {
                if (child.type === 'text') {
                  let txt = (child.content ?? '').replace(/\s+/g, ' ');
                  const prev: any = pieces[pieces.length - 1];
                  const prevEndsWithSpace = prev && 'content' in prev ? /\s$/.test(prev.content) : false;
                  const atStart = !prev || ('break' in prev && prev.break === true);
                  if (atStart || prevEndsWithSpace) {
                    txt = txt.replace(/^\s+/, '');
                  }
                  if (txt.length > 0) {
                    const style: any = {};
                    if (child.style) {
                      const stylesList = child.style.split(' ');
                      if (stylesList.includes('bold')) style.fontWeight = 'bold';
                      if (stylesList.includes('italic')) style.fontStyle = 'italic';
                      if (stylesList.includes('underline')) style.textDecoration = 'underline';
                      if (stylesList.includes('strikethrough')) style.textDecoration = 'line-through';
                      if (stylesList.includes('monospace')) {
                        style.fontFamily = 'Courier';
                        style.color = '#B91C1C';
                      }
                    }
                    pieces.push({ style, content: txt });
                  }
                } else if (child.type === 'break') {
                  pieces.push({ break: true } as any);
                } else {
                  // Fallback: coerce to text
                  let txt = (child.content ?? '').replace(/\s+/g, ' ');
                  const prev: any = pieces[pieces.length - 1];
                  const prevEndsWithSpace = prev && 'content' in prev ? /\s$/.test(prev.content) : false;
                  const atStart = !prev || ('break' in prev && prev.break === true);
                  if (atStart || prevEndsWithSpace) {
                    txt = txt.replace(/^\s+/, '');
                  }
                  if (txt.length > 0) pieces.push({ content: txt });
                }
              });
              // Trim trailing spaces
              for (let i = pieces.length - 1; i >= 0; i--) {
                const p: any = pieces[i];
                if ('content' in p) {
                  p.content = p.content.replace(/\s+$/, '');
                  break;
                } else if ('break' in p) {
                  continue;
                } else {
                  break;
                }
              }
              return pieces.map((p: any, i: number) => {
                if ('break' in p) return <Text key={i}>{'\n'}</Text>;
                return <Text key={i} style={p.style || {}}>{p.content}</Text>;
              });
            })()}
          </Text>
        );
      }
    } else if (element.type === 'list') {
      // Recursive function to render list items with proper nesting
      const renderListItem = (item: PdfListItem, itemIndex: number, ordered: boolean, startNumber: number = 1, depth: number = 0): JSX.Element => {
        const bulletText = ordered ? `${startNumber + itemIndex}.` : '•';
        const bulletWidth = ordered ? Math.max(20, bulletText.length * 8) : 12;

        // Check if content is all inline (text elements only, no blocks)
        const isAllInline = item.content.every(el =>
          el.type === 'text' || el.type === 'break'
        );

        return (
          <View key={itemIndex} style={{ marginBottom: 2 }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              <Text style={{
                width: bulletWidth,
                marginRight: 6,
                textAlign: ordered ? 'right' : 'left',
                flexShrink: 0
              }}>
                {bulletText}
              </Text>
              <View style={{ flex: 1 }}>
                {/* Render the list item content */}
                {item.content.length > 0 ? (
                  isAllInline ? (
                    // For inline content, render as a single Text element to preserve flow
                    <Text style={{ textAlign: 'justify', lineHeight: 1.25 }}>
                      {item.content.map((el, idx) => {
                        if (el.type === 'text') {
                          const style: any = {};
                          if (el.style) {
                            const stylesList = el.style.split(' ');
                            if (stylesList.includes('bold')) style.fontWeight = 'bold';
                            if (stylesList.includes('italic')) style.fontStyle = 'italic';
                            if (stylesList.includes('underline')) style.textDecoration = 'underline';
                            if (stylesList.includes('strikethrough')) style.textDecoration = 'line-through';
                            if (stylesList.includes('monospace')) {
                              style.fontFamily = 'Courier';
                              style.color = '#B91C1C';
                            }
                          }
                          return <Text key={idx} style={style}>{el.content}</Text>;
                        } else if (el.type === 'break') {
                          return <Text key={idx}>{'\n'}</Text>;
                        }
                        return null;
                      })}
                    </Text>
                  ) : (
                    // For block content, render as separate elements
                    <View style={{ textAlign: 'justify', lineHeight: 1.25 }}>
                      {renderPdfElements(item.content)}
                    </View>
                  )
                ) : null}

                {/* Render nested list if present */}
                {item.nestedList && item.nestedList.type === 'list' ? (
                  <View style={{ marginTop: 4, paddingLeft: 12 }}>
                    {(item.nestedList as { type: 'list'; ordered: boolean; items: PdfListItem[]; startNumber?: number }).items.map((nestedItem: PdfListItem, nestedIndex: number) =>
                      renderListItem(
                        nestedItem,
                        nestedIndex,
                        (item.nestedList as { type: 'list'; ordered: boolean; items: PdfListItem[]; startNumber?: number }).ordered,
                        (item.nestedList as { type: 'list'; ordered: boolean; items: PdfListItem[]; startNumber?: number }).startNumber || 1,
                        depth + 1
                      )
                    )}
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        );
      };

      return (
        <View key={index} style={{ marginBottom: 6, paddingLeft: 8 }}>
          {element.items.map((item: PdfListItem, itemIndex: number) =>
            renderListItem(item, itemIndex, element.ordered, element.startNumber || 1)
          )}
        </View>
      );
    } else if (element.type === 'break') {
      return (
        <Text key={index}>{''}</Text>
      );
    } else {
      return null;
    }
  });
}

export function renderPdfElementsPlainText(input: string | string[]): JSX.Element {
  return <Text>{Array.isArray(input) ? input.join('\n') : input}</Text>;
}

export { imageProcessor };